import type { Button as ButtonElement, Column } from "@judo/model-api";
import { MemberType } from "@judo/model-api";
import type { GridColType } from "@mui/x-data-grid";

/**
 * Row action types that are automatically shown inline (featured) when
 * `featuredActions` is 0 (i.e., no explicit featured actions configured).
 *
 * Includes navigation and CRUD operations that benefit from direct visibility:
 * - OpenPageActionDefinition: View/navigate to row detail page
 * - RowOpenPageActionDefinition: Row-level view navigation
 * - RowDeleteActionDefinition: Delete the row (requires `__deleteable` runtime check)
 * - RemoveActionDefinition: Remove from relation
 * - OpenSetSelectorActionDefinition: Set a relation target
 * - UnsetActionDefinition: Unset a relation target
 */
export const AUTO_FEATURED_ROW_ACTION_TYPES = new Set([
	"OpenPageActionDefinition",
	"RowOpenPageActionDefinition",
	"RowDeleteActionDefinition",
	"RemoveActionDefinition",
	"OpenSetSelectorActionDefinition",
	"UnsetActionDefinition",
]);

/**
 * Maps model data types to MUI DataGrid column types.
 *
 * MUI DataGrid column types:
 * - 'string' (default): Text values
 * - 'number': Numeric values with number formatting
 * - 'date': Date values (rendered with date picker)
 * - 'dateTime': DateTime/Timestamp values
 * - 'boolean': Boolean values (rendered with checkbox)
 * - 'singleSelect': Enumeration values (dropdown)
 * - 'actions': Action column (special)
 *
 * @param column The model Column definition
 * @returns The MUI DataGrid column type
 */
export function getColumnType(column: Column): GridColType {
	const dataType = column.attributeType?.dataType?.["@type"];

	if (!dataType) {
		return "string";
	}

	// Type discriminator transforms xsi:type values to simplified names
	// e.g., "data:NumericType" becomes "NumericType"
	// Support both forms for robustness
	switch (dataType) {
		case "NumericType":
		case "data:NumericType":
			return "number";
		case "BooleanType":
		case "data:BooleanType":
			return "boolean";
		case "DateType":
		case "data:DateType":
			return "date";
		case "TimestampType":
		case "data:TimestampType":
			return "dateTime";
		case "EnumerationType":
		case "data:EnumerationType":
			return "singleSelect";
		case "TimeType":
		case "data:TimeType":
			// MUI DataGrid doesn't have a native time type, use string
			return "string";
		default:
			// StringType, PasswordType, BinaryType and their data: prefixed variants
			return "string";
	}
}

/**
 * Gets value options for enumeration columns.
 * @param column The model Column definition
 * @returns Array of value options for singleSelect type, or undefined
 */
export function getEnumerationValueOptions(column: Column): Array<{ value: string; label: string }> | undefined {
	const dataType = column.attributeType?.dataType;
	const typeDiscriminator = dataType?.["@type"];

	// Support both simplified and prefixed type names
	if (typeDiscriminator !== "EnumerationType" && typeDiscriminator !== "data:EnumerationType") {
		return undefined;
	}

	// EnumerationType has members array
	const enumType = dataType as { members?: Array<{ name: string; ordinal: number }> };

	if (!enumType.members || enumType.members.length === 0) {
		return undefined;
	}

	return enumType.members.map((member) => ({
		value: member.name,
		label: member.name, // Could be enhanced to use labels if available
	}));
}

/**
 * Determines if a table column is sortable.
 * A column is sortable if:
 * 1. It has an attributeType
 * 2. The attributeType's dataType is NOT BinaryType
 * 3. The attributeType's memberType is NOT TRANSIENT
 */
export function isColumnSortable(column: Column): boolean {
	if (!column.attributeType) {
		return false;
	}

	const attributeType = column.attributeType;

	// Binary types are not sortable
	if (attributeType.dataType?.["@type"] === "data:BinaryType") {
		return false;
	}

	// Transient members are not sortable
	if (attributeType.memberType === MemberType.TRANSIENT) {
		return false;
	}

	return true;
}

/**
 * Calculates the width needed for an action column based on button content.
 *
 * When `featuredActions` is provided, the calculation accounts for the
 * featured/overflow split pattern (inline buttons + dropdown trigger):
 * - featuredActions = 0: auto-CRUD mode — CRUD buttons shown inline, rest in dropdown.
 *   Width accounts for all CRUD buttons (worst-case, including delete).
 * - featuredActions > 0: featured button widths + dropdown trigger (if overflow)
 * - featuredActions >= total: all buttons inline, no dropdown (same as legacy)
 *
 * When `featuredActions` is not provided, all buttons are treated as inline
 * (backward compatible with legacy behavior).
 *
 * Calculation uses actual MUI small outlined Button metrics:
 * - Text width: ~6.5px per character (13px font-size, proportional sans-serif)
 * - Icon width: 24px (18px icon + 8px margin - 2px offset)
 * - Button padding: 18px (9px left + 9px right for small outlined)
 * - Dropdown trigger: 36px (18px padding + 18px icon)
 * - Spacing: 0px (ButtonGroup collapses borders)
 * - Column padding: 20px (DataGrid cell padding ~10px per side)
 *
 * @param buttons Array of buttons to display in the action column
 * @param featuredActions Number of featured actions to show inline (from ButtonGroup model)
 * @returns Calculated width in pixels
 */
export function calculateActionColumnWidth(buttons: ButtonElement[], featuredActions?: number): number {
	if (buttons.length === 0) {
		return 150; // Default minimum width
	}

	// MUI small outlined Button metrics
	const CHAR_WIDTH = 6.5; // 13px font-size, ~0.5em per char
	const BUTTON_PADDING = 18; // 9px left + 9px right
	const ICON_WIDTH = 24; // 18px icon + 8px margin - 2px offset
	const DROPDOWN_TRIGGER_WIDTH = 36; // 18px padding + 18px icon
	const COLUMN_PADDING = 20; // DataGrid cell padding ~10px per side

	function buttonWidth(button: ButtonElement): number {
		const text = button.label ?? button.name;
		const textWidth = Math.ceil(text.length * CHAR_WIDTH);
		const iconWidth = button.icon ? ICON_WIDTH : 0;
		return textWidth + iconWidth + BUTTON_PADDING;
	}

	// When featuredActions is not provided, treat all buttons as inline (backward compat)
	const effectiveFeatured = featuredActions ?? buttons.length;

	// Auto-featured mode: featuredActions === 0, show standard row action buttons inline
	if (effectiveFeatured === 0) {
		const autoFeaturedButtons = buttons.filter((btn) => {
			const actionType = btn.actionDefinition?.["@type"];
			return actionType != null && AUTO_FEATURED_ROW_ACTION_TYPES.has(actionType);
		});
		const nonFeaturedButtons = buttons.filter((btn) => {
			const actionType = btn.actionDefinition?.["@type"];
			return actionType == null || !AUTO_FEATURED_ROW_ACTION_TYPES.has(actionType);
		});
		const hasOverflow = nonFeaturedButtons.length > 0;

		if (autoFeaturedButtons.length === 0) {
			// No auto-featured buttons — only dropdown trigger
			if (hasOverflow) {
				return DROPDOWN_TRIGGER_WIDTH + COLUMN_PADDING;
			}
			return 150; // Safe default
		}

		// Calculate width for auto-featured buttons (shown inline)
		const featuredWidth = autoFeaturedButtons.reduce((total, button) => total + buttonWidth(button), 0);

		if (hasOverflow) {
			return featuredWidth + DROPDOWN_TRIGGER_WIDTH + COLUMN_PADDING;
		}

		// Only auto-featured buttons, no overflow
		return featuredWidth + COLUMN_PADDING;
	}

	const featuredCount = Math.min(effectiveFeatured, buttons.length);
	const hasOverflow = featuredCount < buttons.length;

	// Calculate width for featured (inline) buttons
	const featuredBtns = buttons.slice(0, featuredCount);
	const buttonsWidth = featuredBtns.reduce((totalWidth, button) => totalWidth + buttonWidth(button), 0);

	if (hasOverflow) {
		return buttonsWidth + DROPDOWN_TRIGGER_WIDTH + COLUMN_PADDING;
	}

	// All buttons are featured, no dropdown
	return buttonsWidth + COLUMN_PADDING;
}
